import React, { useState, useEffect, useRef } from 'react';
import { DynamicComponent, slugToLabel } from '../../../pages/registry/componentRegistry';
import { useMap } from '../MapContext';
import { useApi } from '../../../api/api';
import FleetAnimationControls from '../FleetAnimationControls/FleetAnimationControls';
import './DataLayerTabs.css';

/** Build a layout component shape so DynamicComponent can pass defaultValues from initialLayers (same as column panels). */
function toLayoutComponent(slug, defaultValues, order = 0) {
  return {
    component_slug: slug,
    component_name: slugToLabel(slug),
    component_kind: 'data-layer',
    component_order: order,
    component_default_values: defaultValues ?? null,
  };
}

/**
 * Tab configuration: each tab has an id, label, and list of component slugs to render from the registry.
 */
const TAB_CONFIG = [
  { id: 'oceanography', label: 'Physical Oceanography', slugs: ['enhanced-bathymetry', 'rocky-bottom-type', 'bottom-temperature'] },
  { id: 'biogeochemical', label: 'Biogeochemical', slugs: [] },
  { id: 'ecological', label: 'Ecological', slugs: ['lek', 'trawl-survey'] },
  { id: 'regulatory', label: 'Regulatory', slugs: ['noaa-fishing-closed-areas', 'scallop-areas', 'lobster-management-areas', 'international-fishery-mgmt-areas', 'us-regional-fishery-mgmt-councils', 'us-congressional-districts', 'state-boundaries'] },
  { id: 'industrial', label: 'Industrial', slugs: ['fishing-ports', 'ports-with-landing', 'cold-storage', 'play-groundfish-simulation', 'play-scallop-simulation', 'noaa-navigation-charts', 'vessel-tracking'] },
];

const DataLayerTabs = (props) => {
  const {
    panelHeight = 0.4,
    onActiveTabChange = () => {},
    initialLayers = {},
    viewer: viewerProp,
    fleetMapData = [],
    onFleetDebugUpdate = () => {},
    fleetDataLoading = false,
    fleetAnimationTrigger = 0,
    onFleetAnimationCancel = () => {},
    onLekGridCellClick = () => {},
    onMapEmptyClick = () => {},
    onTrawlSurveyStationClick = () => {},
  } = props;

  const { layerController: mapController, viewer: cesiumViewer } = useMap();
  const { getPorts, getColdStorage } = useApi();
  const viewer = cesiumViewer || viewerProp;
  const [activeTab, setActiveTab] = useState(null);
  const [scallopFleetActive, setScallopFleetActive] = useState(false);

  // Load data for layers that are active on the map (e.g. from initialLayers) via events instead of polling.
  const portsFetchedRef = useRef(false);
  const coldStorageFetchedRef = useRef(false);
  useEffect(() => {
    if (!mapController) return;

    const syncPortsAndColdStorage = () => {
      if (!mapController.isReady()) return;
      const portsActive = mapController.isLayerActive('ports-with-landing');
      const coldStorageActive = mapController.isLayerActive('cold-storage');

      if (portsActive && !portsFetchedRef.current) {
        portsFetchedRef.current = true;
        getPorts()
          .then((res) => {
            const data = res?.data ?? [];
            mapController.setPortsWithLandingData(data);
          })
          .catch((err) => {
            console.error('Error fetching ports data:', err);
            mapController.setPortsWithLandingData([]);
          })
          .finally(() => {});
      } else if (!portsActive) {
        portsFetchedRef.current = false;
        mapController.setPortsWithLandingData([]);
      }

      if (coldStorageActive && !coldStorageFetchedRef.current) {
        coldStorageFetchedRef.current = true;
        getColdStorage()
          .then((res) => {
            const data = res?.data ?? [];
            mapController.setColdStorageData(data);
          })
          .catch((err) => {
            console.error('Error fetching cold storage data:', err);
            mapController.setColdStorageData([]);
          })
          .finally(() => {});
      } else if (!coldStorageActive) {
        coldStorageFetchedRef.current = false;
        mapController.setColdStorageData([]);
      }
    };

    const onLayerChanged = () => syncPortsAndColdStorage();
    const onLayersInitialized = () => syncPortsAndColdStorage();

    syncPortsAndColdStorage();
    window.addEventListener('mapControllerLayerChanged', onLayerChanged);
    window.addEventListener('mapControllerLayersInitialized', onLayersInitialized);
    return () => {
      window.removeEventListener('mapControllerLayerChanged', onLayerChanged);
      window.removeEventListener('mapControllerLayersInitialized', onLayersInitialized);
    };
  }, [mapController, getPorts, getColdStorage]);

  const initialLayerSlugs = Object.keys(initialLayers || {}).filter(
    (s) => initialLayers[s] !== false
  );
  const injections = {
    ...props,
    viewer,
    scallopFleetActive,
    setScallopFleetActive,
    initialLayerSlugs,
  };

  useEffect(() => {
    onActiveTabChange(activeTab !== null);
  }, [activeTab, onActiveTabChange]);

  // Register MapController handlers so map clicks are forwarded to parent
  useEffect(() => {
    if (!mapController?.isReady()) return;
    mapController.setLekGridCellClickHandler(onLekGridCellClick);
    return () => mapController.setLekGridCellClickHandler(null);
  }, [mapController, onLekGridCellClick]);

  useEffect(() => {
    if (!mapController?.isReady()) return;
    mapController.setMapEmptyClickHandler(onMapEmptyClick);
    return () => mapController.setMapEmptyClickHandler(null);
  }, [mapController, onMapEmptyClick]);

  useEffect(() => {
    if (!mapController?.isReady()) return;
    mapController.setTrawlSurveyStationClickHandler(onTrawlSurveyStationClick);
    return () => mapController.setTrawlSurveyStationClickHandler(null);
  }, [mapController, onTrawlSurveyStationClick]);

  const handleTabClick = (tabId) => {
    setActiveTab((prev) => (prev === tabId ? null : tabId));
  };

  const activeConfig = TAB_CONFIG.find((t) => t.id === activeTab);
  const activeSlugs = activeConfig?.slugs ?? [];
  const hasVisibleFleetControls =
    activeSlugs.includes('play-scallop-simulation') ||
    activeSlugs.includes('play-groundfish-simulation');
  const regularFleetActive =
    fleetAnimationTrigger > 0 && fleetAnimationTrigger % 2 === 1;
  const shouldMountFleetAnimationControls =
    initialLayerSlugs.includes('play-scallop-simulation') ||
    initialLayerSlugs.includes('play-groundfish-simulation') ||
    hasVisibleFleetControls ||
    scallopFleetActive ||
    regularFleetActive;

  // Slugs that are in initialLayers (and enabled).
  const initialSlugs = Object.keys(initialLayers || {}).filter(
    (slug) => initialLayers[slug] !== false
  );
  // All slugs from all tabs - mount these in a hidden div so layers load data and activate
  // even before the user opens the tab containing them.
  const allTabSlugs = [...new Set(TAB_CONFIG.flatMap((t) => t.slugs))];
  const hiddenSlugs = [...new Set([...initialSlugs, ...allTabSlugs])];

  return (
    <div className="data-layer-tabs">
      <div className="data-layer-tabs-header">
        <h2 className="section-title bitter-font">Data Layers</h2>
        <div className="section-subtitle" />
      </div>

      <div
        className="data-layer-tabs-content"
        style={activeTab ? { maxHeight: `${panelHeight * 100}vh` } : {}}
      >
        {activeConfig && (
          <div className="tab-panel">
            {activeConfig.slugs.length === 0 ? (
              <div
                className="control-section"
                style={{
                  padding: '40px 20px',
                  textAlign: 'center',
                  color: '#6c757d',
                  fontSize: '14px',
                  fontStyle: 'italic',
                }}
              >
                Coming soon...
              </div>
            ) : (
              activeConfig.slugs.map((slug, idx) => (
                <DynamicComponent
                  key={slug}
                  component={toLayoutComponent(slug, initialLayers?.[slug], idx)}
                  injections={injections}
                />
              ))
            )}
          </div>
        )}
      </div>

      {/* Always mount all tab layer components (hidden) so they load data and update the map; opening a tab shows a second instance for UI that syncs from map controller */}
      {hiddenSlugs.length > 0 && (
        <div aria-hidden="true" style={{ position: 'absolute', left: -9999, width: 1, height: 1, overflow: 'hidden' }}>
          {hiddenSlugs.map((slug, idx) => (
            <DynamicComponent
              key={slug}
              component={toLayoutComponent(slug, initialLayers?.[slug], idx)}
              injections={{ ...injections, isHidden: true }}
            />
          ))}
        </div>
      )}

      {/* Fleet animation: render in visible DOM (not hidden div) so Cesium clock isn't throttled by browser */}
      {shouldMountFleetAnimationControls &&
        viewer && (
          <FleetAnimationControls
            cesiumViewer={viewer}
            mapData={fleetMapData ?? []}
            onDebugUpdate={onFleetDebugUpdate}
            loading={fleetDataLoading}
            animationTrigger={fleetAnimationTrigger}
            onCancelAnimation={onFleetAnimationCancel}
            scallopFleetActive={scallopFleetActive}
            setScallopFleetActive={setScallopFleetActive}
          />
        )}

      <div className="data-layer-tabs-nav">
        {TAB_CONFIG.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`data-layer-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => handleTabClick(tab.id)}
          >
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default DataLayerTabs;
